import { useState, useCallback, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { ToastContext } from './toastContext';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((message: string, type: Toast['type']) => {
    const id = String(++idRef.current);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => remove(id), 4000);
  }, [remove]);

  const success = useCallback((msg: string) => add(msg, 'success'), [add]);
  const error = useCallback((msg: string) => add(msg, 'error'), [add]);
  const info = useCallback((msg: string) => add(msg, 'info'), [add]);

  const iconMap = {
    success: <CheckCircle size={16} color="var(--color-success)" />,
    error: <AlertCircle size={16} color="var(--color-error)" />,
    info: <Info size={16} color="var(--color-primary)" />,
  };

  const bgMap = {
    success: 'rgba(126,231,135,0.1)',
    error: 'rgba(248,81,73,0.1)',
    info: 'rgba(88,166,255,0.1)',
  };

  return (
    <ToastContext.Provider value={{ success, error, info }}>
      {children}
      <div style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        {toasts.map(t => (
          <div
            key={t.id}
            style={{
              background: bgMap[t.type],
              border: `1px solid ${t.type === 'success' ? 'var(--color-success)' : t.type === 'error' ? 'var(--color-error)' : 'var(--color-primary)'}`,
              borderRadius: 'var(--radius-lg)',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 280,
              maxWidth: 'min(400px, calc(100vw - 32px))',
              backdropFilter: 'blur(8px)',
            }}
          >
            {iconMap[t.type]}
            <span style={{ flex: 1, fontSize: 13, wordBreak: 'break-word' }}>{t.message}</span>
            <button
              onClick={() => remove(t.id)}
              style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
